import type { AutocompleteResult } from '@common/types/AutocompleteResult'
import type { Module } from '@common/types/config/Modules'
import type { Children } from '@lc/libreact'
import { Fragment } from 'react/jsx-runtime'
import { Redirect, Route } from 'wouter'
import {
	Boundary,
	CoordBuffer,
	EntryMap,
	EntryMapAround,
	LinesAround,
	MapJourney,
	MapJourneysSwitch,
	OpenedLine,
	PinArea,
} from './components/map/index'
import {
	Around,
	Journeys,
	Lines,
	LineSingle,
	LineTimetable,
	Roadmap,
	Stations,
} from './components/modules'
import { BindStopPopup } from './components/routes/BindStopPopup'
import { LoadJourneys } from './components/routes/LoadJourneys'
import type { ModuleObject } from './util/modules'

export type ModuleRoutes = {
	params?: (
		module: ModuleObject,
		params: Set<string>,
	) => Iterable<string> | null | undefined
	board: (module: ModuleObject) => Children
	map?: (module: ModuleObject) => Children
	route?: (module: ModuleObject) => Children
	searchTarget?: (
		module: ModuleObject,
		result: AutocompleteResult,
	) => string | null
}
export type Routes = Partial<Record<Module, ModuleRoutes>>
export const routes: Routes = {}

const routesJourneys = () => {
	routes.journeys = {
		params: (_module, params) => [
			'from',
			'fromName',
			'to',
			'toName',
			'fromId',
			'fromType',
			'toId',
			'toType',
			'date',
			'bike-type',
			'bike-speed',
			'bike-electric',
			'public-transport',
			'walk',
			...params,
		],
		board: ({ path }) => (
			<Fragment key={path}>
				<Route path={path} children={<Journeys />} />
				<Route
					path={path + '/:id'}
					children={({ id }: { id: string }) => <Roadmap id={id} />}
				/>
			</Fragment>
		),
		map: ({ path }) => (
			<Fragment key={path}>
				<Route path={path} children={<MapJourneysSwitch />} />
				<Route
					path={path + '/:id'}
					children={({ id }: { id: string }) => (
						<MapJourney journeyId={id} highlight />
					)}
				/>
			</Fragment>
		),
		route: ({ path }) => (
			<Fragment key={path}>
				<Route
					path={path}
					children={
						<>
							<BindStopPopup />
							<LoadJourneys />
						</>
					}
				/>
				<Route
					path={path + '/:id'}
					children={
						<>
							<BindStopPopup />
							<LoadJourneys />
						</>
					}
				/>
			</Fragment>
		),
		searchTarget: ({ path }, result) => {
			switch (result.type) {
				case 'area':
				case 'poi':
				case 'address':
				case 'boundary':
					return path
				case 'line':
					return null
			}
		},
	}
}

const routesLines = () => {
	routes.lines = {
		params: (_module, params) => ['name', 'area', 'coord', ...params],
		board: ({ path }) => (
			<Fragment key={path}>
				<Route path={path} children={<Lines />} />
				<Route
					path={path + '/:line/:direction/:stop?'}
					children={({
						line,
						direction,
						stop,
					}: {
						line: string
						direction: string
						stop?: string
					}) => {
						if (direction !== 'forward' && direction !== 'backward')
							return (
								<Redirect
									replace
									to={`${path}/${line}/forward`}
								/>
							)

						return (
							<LineSingle
								line={line}
								direction={direction}
								stop={stop}
							/>
						)
					}}
				/>
				<Route
					path={path + '/:line/:direction/:stop/timetable'}
					children={({
						line,
						direction,
						stop,
					}: {
						line: string
						direction: string
						stop: string
					}) => {
						if (direction !== 'forward' && direction !== 'backward')
							return (
								<Redirect
									replace
									to={`${path}/${line}/forward`}
								/>
							)
						return (
							<LineTimetable
								line={line}
								direction={direction}
								stop={stop}
							/>
						)
					}}
				/>
			</Fragment>
		),
		map: ({ path }) => (
			<Fragment key={path}>
				<Route path={path}>
					<EntryMap />
					<PinArea key={'area'} />
				</Route>
				<Route
					path={path + '/:id/:direction/:stop?'}
					children={({
						id,
						direction,
						stop,
					}: {
						id: string
						direction: 'forward' | 'backward'
						stop?: string
					}) => (
						<OpenedLine id={id} direction={direction} stop={stop} />
					)}
				/>
				<Route
					path={path + '/:line/:direction/:stop/timetable'}
					children={({
						line,
						direction,
					}: {
						line: string
						direction: 'forward' | 'backward'
					}) => <OpenedLine id={line} direction={direction} />}
				/>
			</Fragment>
		),
		route: ({ path }) => (
			<Fragment key={path}>
				<Route path={path} children={() => <BindStopPopup />} />
				<Route
					path={path + '/:id/:direction'}
					children={() => <BindStopPopup />}
				/>
				<Route
					path={path + '/:id/:direction/:stop'}
					children={({ stop }: { stop: string }) => (
						<BindStopPopup stop={stop} />
					)}
				/>
			</Fragment>
		),
		searchTarget: ({ path }, result) => {
			switch (result.type) {
				case 'line':
					return `${path}/${result.line.id}/forward`

				case 'area':
				case 'boundary':
					return path

				case 'poi':
				case 'address':
					return null
			}
		},
	}
}

const routesAround = () => {
	routes.around = {
		params: (_module, params) => [
			'name',
			'area',
			'coord',
			'insee',
			...params,
		],
		board: ({ path }) => (
			<Fragment key={path}>
				<Route path={path} children={<Around />} />
				<Route
					path={path + '/:line/:direction/:stop?'}
					children={({
						line,
						direction,
						stop,
					}: {
						line: string
						direction: string
						stop?: string
					}) => {
						if (direction !== 'forward' && direction !== 'backward')
							return <Redirect replace to={`${path}`} />
						return (
							<LineSingle
								line={line}
								direction={direction}
								stop={stop}
							/>
						)
					}}
				/>
				<Route
					path={path + '/:line/:direction/:stop/timetable'}
					children={({
						line,
						direction,
						stop,
					}: {
						line: string
						direction: string
						stop: string
					}) => {
						if (direction !== 'forward' && direction !== 'backward')
							return <Redirect replace to={`${path}`} />
						return (
							<LineTimetable
								line={line}
								direction={direction}
								stop={stop}
							/>
						)
					}}
				/>
			</Fragment>
		),
		map: ({ path }) => (
			<Fragment key={path}>
				<Route path={path}>
					<EntryMapAround />
					<CoordBuffer />
					<LinesAround />
					<Boundary />
				</Route>
				<Route
					path={path + '/:line/:direction/:stop?'}
					children={({
						line,
						direction,
						stop,
					}: {
						line: string
						direction: string
						stop?: string
					}) => {
						if (direction !== 'forward' && direction !== 'backward')
							return <Redirect replace to={`${path}`} />
						return (
							<>
								<OpenedLine
									id={line}
									direction={direction}
									stop={stop}
								/>
								<CoordBuffer key={'address'} />
							</>
						)
					}}
				/>
				<Route
					path={path + '/:line/:direction/:stop/timetable'}
					children={({
						line,
						direction,
						stop,
					}: {
						line: string
						direction: string
						stop: string
					}) => {
						void stop
						if (direction !== 'forward' && direction !== 'backward')
							return <Redirect replace to={`${path}`} />
						return (
							<>
								<OpenedLine
									id={line}
									direction={direction}
									stop={stop}
								/>
								<CoordBuffer key={'address'} />
							</>
						)
					}}
				/>
			</Fragment>
		),
		route: ({ path }) => (
			<Fragment key={path}>
				<Route path={path} children={() => <BindStopPopup />} />
				<Route
					path={path + '/:line/:direction'}
					children={() => <BindStopPopup />}
				/>
				<Route
					path={path + '/:line/:direction/:stop'}
					children={({ stop }: { stop: string }) => (
						<BindStopPopup stop={stop} />
					)}
				/>
			</Fragment>
		),
		searchTarget: ({ path }, result) => {
			switch (result.type) {
				case 'line':
				case 'area':
					return null

				case 'boundary':
				case 'address':
				case 'poi':
					return path
			}
		},
	}
}

const routesStations = () => {
	routes.stations = {
		board: ({ path }) => (
			<Route key={path} path={path} children={<Stations />} />
		),
		map: ({ path }) => (
			<Route path={path}>
				<EntryMap />
			</Route>
		),
		searchTarget: ({ path }, result) => {
			switch (result.type) {
				case 'address':
				case 'area':
				case 'poi':
				case 'boundary':
					return path

				default:
					return null
			}
		},
	}
}

const loadRoutes = () => {
	routesJourneys()
	routesLines()
	routesAround()
	routesStations()
}
loadRoutes()

const baseParams: Set<string> | string[] = ['pmr', 'lang']

export function moduleParams(module: ModuleObject): Iterable<string> {
	const params = new Set<string>(baseParams)
	return routes[module.module]?.params?.(module, params) ?? params
}

export function boardModule(module: ModuleObject) {
	return routes[module.module]?.board(module) ?? null
}

export function mapModule(module: ModuleObject) {
	return routes[module.module]?.map?.(module) ?? null
}

export function routeModule(module: ModuleObject) {
	return routes[module.module]?.route?.(module) ?? null
}
